/**
 * Content Tags Page
 *
 * Displays media content filtered by a hashtag or content tag in a grid layout.
 * Matches Flutter's content_tags_screen.dart
 *
 * Architecture: Component → useContentTags Hook → UseCase → Service
 *
 * Features:
 * - FlexibleHeaderList with centered icon background (hashtag or label)
 * - 3-column grid of media thumbnails
 * - Infinite scroll pagination
 * - Loading, empty, and error states
 */

import { useParams, useLocation } from 'react-router-dom';
import { useCallback, useRef } from 'react';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage, useGridColumnsCount } from '@/presentation/hooks';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { useContentTags } from './hooks/useContentTags';
import { ContentGridItem } from '@/presentation/components/common/content/ContentGridItem';
import { ContentGridItemState } from '@/presentation/components/common/content/ContentGridItemState';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { Navigator } from '@/presentation/routing/Navigator';
import { CONTENT_CONSTANTS } from '@/presentation/constants/contentConstants';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { qaAttr } from '@/presentation/utils/qaAttr';

export const ContentTagsPage = () => {
  const { tagName: rawTagName } = useParams<{ tagName: string }>();
  const location = useLocation();
  const tagName = rawTagName ?? '';

  // Determine if this is a hashtag or content tag based on location path
  const isHashtag: boolean = location.pathname.startsWith('/hashtag/');

  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const gridColumnsCount = useGridColumnsCount();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  const { medias, isLoading, isLoadingMore, hasMore, error, fetchMedias, loadMore } = useContentTags(tagName, isHashtag);

  useBasePage({
    screenKey,
    onInit: () => {
      fetchMedias();
    },
    cleanReactQueryBehaviour: 'onPop',
  });

  const title = isHashtag ? `#${tagName}` : tagName;
  const iconPath = isHashtag ? '/assets/images/hashtag.png' : '/assets/images/label.png';

  const handleBack = () => {
    Navigator.goBack();
  };

  const handleMediaClick = useCallback((media: UserMediaEntity) => {
    // Prevent navigating to details if media is still processing
    if (media.isProcessing) return;

    Navigator.navigateToContentDetails(media.id);
  }, []);

  const renderMediaItem = useCallback(
    (media: UserMediaEntity) => (
      <ContentGridItem media={media} onClick={handleMediaClick} />
    ),
    [handleMediaClick],
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'content-tags', status: isLoading ? 'loading' : 'ready' }}
      listQa={{ id: 'content-tags-list' }}
      items={medias}
      renderItem={renderMediaItem}
      getItemKey={(media) => media.id}
      gridProps={{
        columnsCount: gridColumnsCount,
        crossAxisSpacing: CONTENT_CONSTANTS.gridLayout.crossAxisSpacing,
        mainAxisSpacing: CONTENT_CONSTANTS.gridLayout.mainAxisSpacing,
        itemAspectRatio: CONTENT_CONSTANTS.gridLayout.itemAspectRatio,
        bufferRows: CONTENT_CONSTANTS.gridLayout.bufferRows,
      }}
      onLoadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      emptyListComponent={
        <ContentGridItemState
          isLoading={isLoading}
          errorText={error ? l10n.contentTags.errorLoading : undefined}
          emptyText={l10n.contentTags.noContent}
        />
      }
      backgroundColor={theme.background}
      appBarOptions={{
        title,
        onLeadingTap: handleBack,
        actions: (
          <button
            {...qaAttr({ id: 'content-tags-add' })}
            onClick={() => { /* No-op: add content action not implemented on web */ }}
            className="hover:opacity-80 transition-opacity focus:outline-none"
            style={{ marginLeft: responsive.spacing.lg }}
            aria-label="Add content"
          >
            <AssetIcon path="/assets/images/add.png" width={responsive.icon.medium} height={responsive.icon.medium} color={theme.icon.primary} />
          </button>
        ),
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              // layoutOrientation="center"
              title={tagName}
              iconPath={iconPath}
            />
          ),
        },
      }}
    />
  );
};
